import { Tooltip } from "react-native-paper";
import {
    Alert,
    Button,
    CheckBox,
    DataTable,
    Icon,
    IconButton,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    Text,
    TooltipErrorIcon,
    TooltipWarningIcon,
    useAuth,
    useMerchantConfig,
    useModal,
    usePagination,
    useSearch,
    useTheme,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import {
    GQShiftRm,
    useAddEconomicSyncJobsMutation,
    useIntegrationsOfTypeQuery,
    useShiftsQuery,
} from "graphql-sdk";
import {
    SearchDefinition,
    formatDateTime,
    today,
    formatRelativeDate,
} from "lib";
import { formatAmount } from "lib";
import React, { View } from "react-native";
import { useTranslation } from "locales";

import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";

import type { AvailableLocale } from "locales";
import { useSearchDefinition } from "../../hooks";
import { useCallback, useState } from "react";
import { EconomicVoucherTextModal } from "./voucher-text-modal";
import { getAlertText } from "./shift-screen";
type ScreenProps = RootStackScreenProps<"SHIFTS">;

export function ShiftListScreen({ navigation: { navigate } }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.shift");

    const { currency } = useMerchantConfig();
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const theme = useTheme();
    const toast = useToast();
    const { render } = useModal();
    const { createSearchDefinition } = useSearchDefinition();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "opened_at",
        initialSortDirection: "DESC",
    });

    const defaultShiftSearch: SearchDefinition = {
        shiftNumber: {
            name: t("searching.shift_number", "Shift number"),
            type: "integer",
            value: 0,
            enabled: false,
            // TODO: It would be awesome to be able to configure if a "Number/Integer" field allows negative input (and possibly: if a Number input allows float or only integer input - that would remove the need for an Integer input).
        },
        amount: createSearchDefinition("amount"),
        openedAt: {
            name: t("searching.opened_between", "Opened between"),
            type: "date",
            values: { from: today(new Date()), to: today(new Date()) },
            enabled: false,
            description: t(
                "searching.opened_between_description",
                "Click here to select two dates and times to search between"
            ),
        },
        closedAt: {
            name: t("searching.closed_between", "Closed between"),
            type: "date",
            values: { from: today(new Date()), to: today(new Date()) },
            enabled: false,
            description: t(
                "searching.closed_between_description",
                "Click here to select two dates and times to search between"
            ),
        },
        cashRegisterName: {
            name: t("searching.cash_register_name", "Cash register name"),
            type: "string",
            value: "",
            enabled: false,
        },
        department: createSearchDefinition("department"),
    };

    const {
        component: searchComponent,
        indicator: searchIndicator,
        search,
    } = useSearch(defaultShiftSearch, {
        visible: true,
        onSubmit: () => {
            // When the search form is submitted, then return the pagination to the first page.
            onPageChange(0);
        },
    });

    const { data, loading, error } = useShiftsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            searching: search,
        },
        fetchPolicy: "no-cache",
    });

    const [{ locale }] = useAdminSession(["locale"]);
    const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);
    const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false);

    const handleMultiSelect = useCallback(
        (selected: boolean, itemIds: string[]) => {
            let clonedSelectedDiscounts = [...selectedShiftIds];
            if (selected) {
                clonedSelectedDiscounts = [
                    ...clonedSelectedDiscounts,
                    ...itemIds,
                ];
            } else {
                clonedSelectedDiscounts = clonedSelectedDiscounts.filter(
                    i => !itemIds.includes(i)
                );
            }
            setSelectedShiftIds(clonedSelectedDiscounts);
        },
        [selectedShiftIds]
    );

    const economicIntegration = useIntegrationsOfTypeQuery({
        variables: {
            type: "E-CONOMIC",
        },
        fetchPolicy: "no-cache",
    });

    const fiskalyIntegration = useIntegrationsOfTypeQuery({
        variables: {
            type: "FISKALY",
        },
        fetchPolicy: "no-cache",
    });

    const [addEconomicSyncJob] = useAddEconomicSyncJobsMutation();

    const handleAddEconomicSyncJob = useCallback(
        async (shiftIds: GQShiftRm["id"][]) => {
            if (
                !economicIntegration ||
                economicIntegration.loading ||
                economicIntegration.data.integrationsOfType.data.length !== 1
            ) {
                return;
            }

            // Ask for a text to use when sync'ing the shift
            let voucherTextModalResponse: {
                sync: boolean;
                voucherText?: string;
            } = await render(onClose => (
                <EconomicVoucherTextModal onClose={onClose} />
            ));

            if (
                !voucherTextModalResponse ||
                voucherTextModalResponse.sync === false
            ) {
                return;
            }

            const result = await addEconomicSyncJob({
                variables: {
                    ids: shiftIds,
                    integrationId:
                        economicIntegration.data.integrationsOfType.data.find(
                            itr => itr.active && itr.apiKey
                        )?.id,
                    voucherText: voucherTextModalResponse.voucherText,
                },
            });

            if (result && result.data.addEconomicSyncJobs.status === true) {
                toast.success(
                    t(
                        "backoffice.shifts.add_syncjob_success",
                        "A synchronization of the shift was added to the queue."
                    )
                );
            } else {
                toast.warning(
                    t(
                        "backoffice.shifts.add_syncjob_failed",
                        "Adding a sync job failed. Try again later."
                    )
                );
            }
        },
        [addEconomicSyncJob, economicIntegration, render, t, toast]
    );

    const handleSyncSelected = useCallback(async () => {
        if (selectedShiftIds.length <= 0) {
            return;
        }

        await handleAddEconomicSyncJob(selectedShiftIds);
    }, [handleAddEconomicSyncJob, selectedShiftIds]);

    const hasFiskalyIntegration =
        fiskalyIntegration?.data?.integrationsOfType.data.length === 1 || false;

    const hasEconomicIntegration =
        economicIntegration?.data?.integrationsOfType.data.length === 1 ||
        false;

    const hasSyncIntegration =
        hasEconomicIntegration || hasFiskalyIntegration || false;

    const handleShowShift = useCallback(
        (id: string) => {
            navigate("SHIFT", {
                id,
            });
        },
        [navigate]
    );

    const renderRow = useCallback(
        (item, rowIndex) => (
            <DataTable.Row
                key={item?.id}
                onPress={() => item && handleShowShift(item.id)}
                style={rowIndex % 2 === 1 ? styles.oddRow : undefined}
            >
                {hasEconomicIntegration ? (
                    <DataTable.Cell style={styles.checkboxColumn}>
                        <CheckBox
                            value={selectedShiftIds.includes(item.id)}
                            onValueChange={value =>
                                handleMultiSelect(value, [item.id])
                            }
                            testID={"select:" + item.id}
                        />
                    </DataTable.Cell>
                ) : null}
                <DataTable.Cell style={styles.shiftNumberColumn}>
                    {item.shiftNumber}
                </DataTable.Cell>
                <DataTable.Cell>
                    {item && item.cashRegister.department?.name}
                </DataTable.Cell>
                <DataTable.Cell>
                    {item && (item.cashRegisterName || item.cashRegister.name)}
                </DataTable.Cell>
                <DataTable.Cell style={styles.dateColumn}>
                    <View style={styles.flex1}>
                        <Tooltip
                            title={`${t(
                                "backoffice.shifts.opened_at",
                                "Opened"
                            )} ${formatDateTime(
                                item.openedAt,
                                locale as AvailableLocale
                            )}`}
                        >
                            <Text numberOfLines={1}>
                                {formatDateTime(
                                    item.openedAt,
                                    locale as AvailableLocale
                                )}
                            </Text>
                        </Tooltip>
                    </View>
                </DataTable.Cell>
                <DataTable.Cell style={styles.dateColumn}>
                    {item.closedAt ? (
                        <View style={styles.flex1}>
                            <Tooltip
                                title={`${t(
                                    "backoffice.shifts.closed_at",
                                    "Closed"
                                )} ${formatDateTime(
                                    item.closedAt,
                                    locale as AvailableLocale
                                )}`}
                            >
                                <Text numberOfLines={1}>
                                    {formatDateTime(
                                        item.closedAt,
                                        locale as AvailableLocale
                                    )}
                                </Text>
                            </Tooltip>
                        </View>
                    ) : null}
                </DataTable.Cell>
                <DataTable.Cell>{item?.openedByUser.username}</DataTable.Cell>
                <DataTable.Cell>{item?.closedByUser?.username}</DataTable.Cell>
                {hasSyncIntegration ? (
                    <DataTable.Cell style={styles.dateColumn}>
                        {item.syncedAt ? (
                            item.syncStatus === "SUCCESS" ? (
                                <View style={styles.flex1}>
                                    <Tooltip
                                        title={`${t(
                                            "backoffice.shifts.synced_at",
                                            "Sync'ed"
                                        )} ${formatDateTime(
                                            item.syncedAt,
                                            locale as AvailableLocale
                                        )}`}
                                    >
                                        <Text numberOfLines={1}>
                                            {formatDateTime(
                                                item.syncedAt,
                                                locale as AvailableLocale
                                            )}
                                        </Text>
                                    </Tooltip>
                                </View>
                            ) : item.syncStatus === "FAIL" ? (
                                <View
                                    style={[theme.styles.row, styles.rowCenter]}
                                >
                                    <TooltipWarningIcon
                                        title={getAlertText(
                                            item.syncDetails,
                                            t(
                                                "backoffice.shifts.sync_unknown_details",
                                                "Sync caused problems. Check the log."
                                            )
                                        )}
                                        style={styles.noSpace}
                                    />
                                    <Spacer />
                                    <Text numberOfLines={1}>
                                        {formatRelativeDate(
                                            item.syncedAt,
                                            locale as AvailableLocale
                                        )}
                                    </Text>
                                </View>
                            ) : item.syncStatus === "ERROR" ? (
                                <View
                                    style={[theme.styles.row, styles.rowCenter]}
                                >
                                    <TooltipErrorIcon
                                        title={getAlertText(
                                            item.syncDetails,
                                            t(
                                                "backoffice.shifts.sync_unknown_details",
                                                "Sync caused problems. Check the log."
                                            )
                                        )}
                                        style={styles.noSpace}
                                    />
                                    <Spacer />
                                    <Text numberOfLines={1}>
                                        {formatRelativeDate(
                                            item.syncedAt,
                                            locale as AvailableLocale
                                        )}
                                    </Text>
                                </View>
                            ) : null
                        ) : null}
                    </DataTable.Cell>
                ) : null}
                <DataTable.Cell numeric style={styles.dateColumn}>
                    {item &&
                        formatAmount(item.amount, currency, {
                            locale: locale as AvailableLocale,
                        })}
                </DataTable.Cell>
                <DataTable.Cell
                    numeric
                    style={
                        hasEconomicIntegration
                            ? styles.sortIcon
                            : styles.singleIconColumn
                    }
                >
                    {!hasEconomicIntegration || !item.closedAt ? null : (
                        <IconButton
                            name="sync"
                            onPress={() => handleAddEconomicSyncJob([item.id])}
                            color={styles.detailsIcon.color}
                        />
                    )}
                    <IconButton
                        color={styles.detailsIcon.color}
                        name="shift"
                        onPress={() => item && handleShowShift(item.id)}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            currency,
            handleAddEconomicSyncJob,
            handleMultiSelect,
            handleShowShift,
            hasEconomicIntegration,
            hasSyncIntegration,
            locale,
            selectedShiftIds,
            styles.checkboxColumn,
            styles.dateColumn,
            styles.detailsIcon.color,
            styles.flex1,
            styles.noSpace,
            styles.oddRow,
            styles.rowCenter,
            styles.shiftNumberColumn,
            styles.singleIconColumn,
            styles.sortIcon,
            t,
            theme.styles.row,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <View style={[theme.styles.row, styles.container]}>
                {hasEconomicIntegration ? (
                    <Button
                        iconName="sync"
                        onPress={handleSyncSelected}
                        disabled={selectedShiftIds.length <= 0}
                    >
                        {t("backoffice.shifts.sync_selected", "Sync selected")}
                    </Button>
                ) : null}
                {searchIndicator}
            </View>
            <Spacer space={2} />
            <Surface>
                {searchComponent}
                <DataTable>
                    <DataTable.Header>
                        {hasEconomicIntegration ? (
                            <DataTable.Title style={styles.checkboxColumn}>
                                <CheckBox
                                    value={allRowsSelected}
                                    onValueChange={value => {
                                        const allItemIds =
                                            data!.shifts.data.map(p => p.id);
                                        handleMultiSelect(value, allItemIds);
                                        setAllRowsSelected(value);
                                    }}
                                />
                            </DataTable.Title>
                        ) : null}

                        <DataTable.Title
                            sortDirection={
                                sortBy === "shiftNumber"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("shiftNumber")}
                            style={styles.shiftNumberColumn}
                        >
                            {t("backoffice.shifts.shift_number", "Shift no.")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "departmentName"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("departmentName")}
                        >
                            {t(
                                "backoffice.shifts.department_name",
                                "Department"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "cashRegisterName"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("cashRegisterName")}
                        >
                            {t(
                                "backoffice.common.cash_register_name",
                                "Cash Register"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "openedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("openedAt")}
                            style={styles.dateColumn}
                        >
                            {t("backoffice.shifts.opened_at", "Opened")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "closedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("closedAt")}
                            style={styles.dateColumn}
                        >
                            {t("backoffice.shifts.closed_at", "Closed")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "openedByUsername"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("openedByUsername")}
                        >
                            {t(
                                "backoffice.shifts.opened_by_username",
                                "Opened by"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "closedByUsername"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("closedByUsername")}
                        >
                            {t(
                                "backoffice.shifts.closed_by_username",
                                "Closed by"
                            )}
                        </DataTable.Title>
                        {hasSyncIntegration ? (
                            <DataTable.Title style={styles.dateColumn}>
                                {t("backoffice.shifts.synced_at", "Sync'ed")}
                            </DataTable.Title>
                        ) : null}
                        <DataTable.Title
                            numeric
                            sortDirection={
                                sortBy === "amount" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("amount")}
                            style={styles.dateColumn}
                        >
                            {t("common.amount", "Amount")}
                        </DataTable.Title>
                        <DataTable.Title
                            numeric
                            style={
                                hasEconomicIntegration
                                    ? styles.sortIcon
                                    : styles.singleIconColumn
                            }
                        >
                            <Icon name="sort" color={styles.sortIcon.color} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? <Loading /> : data?.shifts.data.map(renderRow)}

                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.shifts.pagination.pages}
                        itemCount={data?.shifts.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = (theme, dimensions) => ({
    container: {
        justifyContent: "flex-end",
        columnGap: theme.spacingScale,
    },

    checkboxColumn: {
        flexBasis: theme.spacingScale * 4,
        flexShrink: 0,
        flexGrow: 0,
        alignItems: "center",
    },

    rowCenter: {
        alignItems: "center",
    },

    noSpace: {
        paddingHorizontal: 0,
    },
    sortIcon: {
        flexBasis: theme.spacingScale * 8,
        flexShrink: 0,
        flexGrow: 0,
        color: theme.colors.black,
    },
    singleIconColumn: {
        flexBasis: theme.spacingScale * 4,
        flexShrink: 0,
        flexGrow: 0,
    },
    shiftNumberColumn: {
        flexBasis: theme.spacingScale * 8,
        flexShrink: 0,
        flexGrow: 0,
    },

    detailsIcon: {
        color: theme.colors.secondary,
    },

    oddRow: {
        ...theme.styles.oddRow,
    },

    flex1: {
        ...theme.styles.flex1,
    },

    dateColumn: {
        flexBasis: dimensions.width < theme.breakpoint.portraitWidth ? 120 : 0,
        flexShrink: dimensions.width < theme.breakpoint.portraitWidth ? 0 : 1,
        flexGrow: dimensions.width < theme.breakpoint.portraitWidth ? 0 : 1,
    },

    success: {
        color: theme.colors.success,
    },
    warning: {
        color: theme.colors.warning,
    },
    error: {
        color: theme.colors.error,
    },
});
